import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { ContactFormFields } from "../components/questionnaire/ContactFormFields";
import { QuestionStep } from "../components/questionnaire/QuestionStep";
import { QuestionnaireActions } from "../components/questionnaire/QuestionnaireActions";
import { QuestionnaireHeader } from "../components/questionnaire/QuestionnaireHeader";
import { TurnstileWidget } from "../components/questionnaire/TurnstileWidget";
import { images } from "../config/images";
import {
  createEmptyAnswers,
  emptyContactDetails,
  QUESTION_COUNT,
  questionnaireQuestionIds,
  questionnaireQuestions,
} from "../data/questionnaire";
import type {
  ContactDetails,
  ContactErrors,
  QuestionnaireAnswer,
  QuestionnaireAnswers,
  QuestionnaireDraft,
  QuestionnaireQuestion,
} from "../types/questionnaire";
import "../styles/questionnaire.css";
import {
  trackContactStarted,
  trackFormCompleted,
  trackFormError,
  trackFormStarted,
  trackQuestionAnswered,
  trackQuestionViewed,
  type FormErrorType,
} from "../lib/analytics";

const STORAGE_KEY = "evolution-questionnaire-progress-v1";
const FINAL_STEP = QUESTION_COUNT;
const logoUrl = images.logo.src;
const SUBMISSION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type SubmissionStatus = "idle" | "loading" | "error" | "success";

function createSubmissionId(): string {
  if (typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }

  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
}

function getEmptyDraft(): QuestionnaireDraft {
  return {
    version: 1,
    submissionId: createSubmissionId(),
    step: 0,
    answers: createEmptyAnswers(),
    details: { ...emptyContactDetails },
    privacyAccepted: false,
  };
}

function readDraft(): QuestionnaireDraft {
  const emptyDraft = getEmptyDraft();

  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return emptyDraft;

    const parsed = JSON.parse(stored) as Partial<QuestionnaireDraft>;
    if (parsed.version !== 1) return emptyDraft;

    const answers = createEmptyAnswers();
    questionnaireQuestionIds.forEach((id) => {
      const candidate = parsed.answers?.[id];
      if (!candidate || typeof candidate !== "object") return;
      answers[id] = {
        value:
          typeof candidate.value === "string"
            ? candidate.value.slice(0, 1_500)
            : "",
        other:
          typeof candidate.other === "string" ? candidate.other.slice(0, 300) : "",
      };
    });

    const details = { ...emptyContactDetails };
    (Object.keys(details) as Array<keyof ContactDetails>).forEach((field) => {
      const candidate = parsed.details?.[field];
      if (typeof candidate === "string") details[field] = candidate.slice(0, 160);
    });

    const storedStep = Number.isInteger(parsed.step) ? Number(parsed.step) : 0;

    return {
      version: 1,
      submissionId:
        typeof parsed.submissionId === "string" &&
        SUBMISSION_ID_PATTERN.test(parsed.submissionId)
          ? parsed.submissionId.toLowerCase()
          : emptyDraft.submissionId,
      step: Math.min(Math.max(storedStep, 0), FINAL_STEP),
      answers,
      details,
      privacyAccepted: parsed.privacyAccepted === true,
    };
  } catch {
    return emptyDraft;
  }
}

function validateQuestion(
  question: QuestionnaireQuestion,
  answer: QuestionnaireAnswer,
): string | undefined {
  if (!answer.value.trim()) {
    return question.type === "longText"
      ? "Cuéntame un poco más para poder continuar."
      : "Selecciona una opción para continuar.";
  }

  if (
    question.type === "single" &&
    question.allowOther &&
    answer.value === "Otro" &&
    !answer.other.trim()
  ) {
    return "Escribe brevemente cuál es tu respuesta.";
  }

  return undefined;
}

function validateDetails(
  details: ContactDetails,
  privacyAccepted: boolean,
  turnstileToken: string,
): ContactErrors {
  const errors: ContactErrors = {};
  const age = Number(details.age);
  const phoneDigits = details.whatsapp.replace(/\D/g, "");
  const instagram = details.instagram.trim().replace(/^@/, "");

  if (!details.name.trim()) {
    errors.name = "Escribe tu nombre.";
  } else if (details.name.trim().length > 100) {
    errors.name = "El nombre es demasiado largo.";
  }

  if (!details.age.trim()) {
    errors.age = "Escribe tu edad.";
  } else if (!Number.isInteger(age) || age < 16 || age > 100) {
    errors.age = "Introduce una edad válida entre 16 y 100 años.";
  }

  if (!details.email.trim()) {
    errors.email = "Escribe tu email.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(details.email.trim())) {
    errors.email = "Introduce un email válido.";
  }

  if (!details.whatsapp.trim()) {
    errors.whatsapp = "Escribe tu número de WhatsApp.";
  } else if (
    !/^[+\d][\d\s().-]+$/.test(details.whatsapp.trim()) ||
    phoneDigits.length < 7 ||
    phoneDigits.length > 15
  ) {
    errors.whatsapp = "Introduce un número válido, con prefijo si es internacional.";
  }

  if (instagram && !/^[a-zA-Z0-9._]{1,30}$/.test(instagram)) {
    errors.instagram = "Escribe solo tu usuario, por ejemplo @usuario.";
  }

  if (!privacyAccepted) {
    errors.privacy = "Necesitamos tu aceptación para enviar la solicitud.";
  }

  if (!turnstileToken) {
    errors.turnstile = "Completa la verificación de seguridad.";
  }

  return errors;
}

function focusFirstError(errors: ContactErrors) {
  const firstField = Object.keys(errors)[0];
  const targetId =
    firstField === "privacy"
      ? "contact-privacy"
      : firstField === "turnstile"
        ? "contact-turnstile"
        : `contact-${firstField}`;

  window.requestAnimationFrame(() => {
    document.getElementById(targetId)?.focus();
    document.getElementById(targetId)?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "center",
    });
  });
}

export default function Questionnaire() {
  const initialDraftRef = useRef<QuestionnaireDraft | null>(null);
  if (!initialDraftRef.current) initialDraftRef.current = readDraft();
  const initialDraft = initialDraftRef.current;
  const submissionId = initialDraft.submissionId;

  const [step, setStep] = useState(initialDraft.step);
  const [answers, setAnswers] = useState<QuestionnaireAnswers>(
    initialDraft.answers,
  );
  const [details, setDetails] = useState<ContactDetails>(initialDraft.details);
  const [privacyAccepted, setPrivacyAccepted] = useState(
    initialDraft.privacyAccepted,
  );
  const [questionError, setQuestionError] = useState<string>();
  const [contactErrors, setContactErrors] = useState<ContactErrors>({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [submissionStatus, setSubmissionStatus] =
    useState<SubmissionStatus>("idle");
  const [submissionError, setSubmissionError] = useState("");

  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as
    | string
    | undefined;
  const isDetailsStep = step === FINAL_STEP;
  const currentQuestion = questionnaireQuestions[step];

  useEffect(() => {
    if (submissionStatus === "success") return;
    if (isDetailsStep) trackContactStarted(submissionId);
    else trackQuestionViewed(submissionId, step);
  }, [isDetailsStep, step, submissionId, submissionStatus]);

  useEffect(() => {
    if (submissionStatus === "success") return;

    const draft: QuestionnaireDraft = {
      version: 1,
      submissionId,
      step,
      answers,
      details,
      privacyAccepted,
    };

    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch {
      // The questionnaire still works when storage is disabled by the browser.
    }
  }, [answers, details, privacyAccepted, step, submissionId, submissionStatus]);

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(() => {
      document.getElementById("questionnaire-step-title")?.focus({
        preventScroll: true,
      });
      window.scrollTo({
        top: 0,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [step]);

  const handleTokenChange = useCallback((token: string) => {
    setTurnstileToken(token);
    if (token) {
      setContactErrors((current) => ({ ...current, turnstile: undefined }));
    }
  }, []);

  const updateAnswer = (answer: QuestionnaireAnswer) => {
    if (!currentQuestion) return;
    trackFormStarted(submissionId);
    setAnswers((current) => ({
      ...current,
      [currentQuestion.id]: answer,
    }));
    setQuestionError(undefined);
  };

  const handleQuestionSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!currentQuestion) return;

    const error = validateQuestion(
      currentQuestion,
      answers[currentQuestion.id],
    );
    if (error) {
      setQuestionError(error);
      window.requestAnimationFrame(() => {
        document.getElementById(`${currentQuestion.id}-error`)?.focus();
      });
      return;
    }

    setQuestionError(undefined);
    trackQuestionAnswered(submissionId, step);
    setStep((current) => Math.min(current + 1, FINAL_STEP));
  };

  const handleBack = () => {
    setQuestionError(undefined);
    setSubmissionError("");
    setSubmissionStatus("idle");

    if (step === 0) {
      window.location.assign("/");
      return;
    }

    setStep((current) => current - 1);
  };

  const handleDetailsChange = (nextDetails: ContactDetails) => {
    trackFormStarted(submissionId);
    const changedField = (Object.keys(details) as Array<keyof ContactDetails>).find(
      (field) => details[field] !== nextDetails[field],
    );
    setDetails(nextDetails);
    if (changedField) {
      setContactErrors((current) => ({
        ...current,
        [changedField]: undefined,
      }));
    }
    if (submissionStatus === "error") setSubmissionStatus("idle");
  };

  const handleFinalSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submissionStatus === "loading") return;

    const firstIncompleteQuestion = questionnaireQuestions.findIndex((question) =>
      validateQuestion(question, answers[question.id]),
    );
    if (firstIncompleteQuestion >= 0) {
      const incompleteQuestion = questionnaireQuestions[firstIncompleteQuestion];
      setStep(firstIncompleteQuestion);
      setQuestionError(
        validateQuestion(incompleteQuestion, answers[incompleteQuestion.id]),
      );
      return;
    }

    const errors = validateDetails(details, privacyAccepted, turnstileToken);
    setContactErrors(errors);
    if (Object.keys(errors).length > 0) {
      focusFirstError(errors);
      return;
    }

    setSubmissionStatus("loading");
    setSubmissionError("");

    const payload = {
      submissionId,
      answers: {
        goal: answers.goal.value,
        goalOther: answers.goal.value === "Otro" ? answers.goal.other.trim() : "",
        difficulty: answers.difficulty.value,
        difficultyOther:
          answers.difficulty.value === "Otro"
            ? answers.difficulty.other.trim()
            : "",
        currentSituation: answers.currentSituation.value.trim(),
        previousPlans: answers.previousPlans.value,
        wellbeingDuration: answers.wellbeingTimeline.value,
        needs: answers.neededSupport.value,
        professionalExperience: answers.professionalExperience.value,
        commitment: answers.commitment.value,
        weeklyTime: answers.weeklyTime.value,
        preferredDuration: answers.preferredDuration.value,
        mostValued: answers.supportPriorities.value,
        whyNow: answers.whyNow.value.trim(),
        investmentReadiness: answers.investmentReadiness.value,
      },
      details: {
        name: details.name.trim(),
        age: Number(details.age),
        email: details.email.trim().toLowerCase(),
        whatsapp: details.whatsapp.trim(),
        instagram: details.instagram.trim(),
      },
      privacyAccepted,
      turnstileToken,
    };

    let submissionErrorType: FormErrorType = "network";
    try {
      const response = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = (await response.json().catch(() => null)) as
        | { success?: boolean; error?: string; submissionId?: string; storageConfirmed?: boolean }
        | null;

      submissionErrorType = response.ok ? "invalid_response" : "http";
      if (!response.ok || result?.success !== true || result.submissionId !== submissionId) {
        throw new Error(
          typeof result?.error === "string" && result.error.length < 240
            ? result.error
            : "No hemos podido enviar tu solicitud. Inténtalo de nuevo.",
        );
      }

      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // Nothing else is required when storage is unavailable.
      }
      setSubmissionStatus("success");
      // The backend preserves its accepted-POST fallback. Only an explicit,
      // matching Apps Script acknowledgement is an analytics conversion.
      if (result.storageConfirmed === true) trackFormCompleted(submissionId);
    } catch (error) {
      trackFormError(submissionErrorType);
      const fallbackMessage =
        "No hemos podido enviar tu solicitud. Revisa tu conexión e inténtalo de nuevo.";
      const candidateMessage = error instanceof Error ? error.message : "";
      setSubmissionError(
        candidateMessage &&
          !/failed to fetch|networkerror|load failed|fetch failed/i.test(
            candidateMessage,
          )
          ? candidateMessage
          : fallbackMessage,
      );
      if (import.meta.env.DEV) {
        console.error("Questionnaire submission failed", {
          submissionId,
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }
      setSubmissionStatus("error");
      setTurnstileToken("");
      setTurnstileResetKey((current) => current + 1);
    }
  };

  if (submissionStatus === "success") {
    return (
      <main className="questionnaire-page questionnaire-page--success">
        <section className="questionnaire-success" aria-labelledby="success-title">
          <a
            className="questionnaire-logo questionnaire-success__logo"
            href="/"
            aria-label="Evolution by Corina, volver al inicio"
          >
            <img src={logoUrl} alt="Evolution by Corina" />
          </a>
          <span className="questionnaire-success__mark" aria-hidden="true">
            ✓
          </span>
          <p className="questionnaire-eyebrow">Gracias por confiar en Evolution</p>
          <h1 id="success-title">SOLICITUD ENVIADA 🤍</h1>
          <div className="questionnaire-success__message">
            <p>Gracias por dar el primer paso hacia tu evolución.</p>
            <p>
              Me pondré en contacto contigo por WhatsApp lo antes posible para
              agendar una videollamada, conocernos y explicarte los siguientes
              pasos.
            </p>
            <p>
              Mientras tanto, puedes volver a la página principal y seguir
              descubriendo Evolution.
            </p>
          </div>
          <a className="questionnaire-button questionnaire-button--primary" href="/">
            Volver al inicio
            <span aria-hidden="true">→</span>
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="questionnaire-page" data-clarity-mask="true">
      <QuestionnaireHeader
        logoUrl={logoUrl}
        currentQuestion={Math.min(step + 1, QUESTION_COUNT)}
        totalQuestions={QUESTION_COUNT}
        isDetailsStep={isDetailsStep}
      />

      <div className="questionnaire-main">
        {!isDetailsStep && currentQuestion ? (
          <form
            className="questionnaire-stage"
            key={currentQuestion.id}
            aria-labelledby="questionnaire-step-title"
            noValidate
            onSubmit={handleQuestionSubmit}
          >
            <QuestionStep
              question={currentQuestion}
              answer={answers[currentQuestion.id]}
              error={questionError}
              onChange={updateAnswer}
            />
            <QuestionnaireActions
              onBack={handleBack}
              isFirstStep={step === 0}
            />
          </form>
        ) : (
          <form
            className="questionnaire-stage questionnaire-stage--contact"
            key="contact-details"
            aria-labelledby="questionnaire-step-title"
            noValidate
            onSubmit={handleFinalSubmit}
          >
            <div className="questionnaire-question__heading questionnaire-contact-heading">
              <p className="questionnaire-eyebrow">Ya casi está</p>
              <h1 id="questionnaire-step-title" tabIndex={-1}>
                ¡Cuéntame un poquito más sobre ti! 🤍
              </h1>
              <p className="questionnaire-helper">
                Estos datos nos permitirán revisar tu solicitud y contactar contigo.
              </p>
            </div>

            <ContactFormFields
              details={details}
              errors={contactErrors}
              privacyAccepted={privacyAccepted}
              onDetailsChange={handleDetailsChange}
              onPrivacyChange={(accepted) => {
                setPrivacyAccepted(accepted);
                setContactErrors((current) => ({
                  ...current,
                  privacy: undefined,
                }));
              }}
            />

            <TurnstileWidget
              siteKey={turnstileSiteKey}
              resetKey={turnstileResetKey}
              error={contactErrors.turnstile}
              onTokenChange={handleTokenChange}
            />

            {submissionError ? (
              <div className="questionnaire-submit-error" role="alert">
                <strong>No se ha podido enviar</strong>
                <p>{submissionError}</p>
                <span>Tus respuestas siguen guardadas. Puedes volver a intentarlo.</span>
              </div>
            ) : null}

            <QuestionnaireActions
              onBack={handleBack}
              continueLabel="Enviar solicitud"
              isLoading={submissionStatus === "loading"}
            />
          </form>
        )}
      </div>

      <footer className="questionnaire-footer">
        <span>© {new Date().getFullYear()} Evolution by Corina</span>
        <div>
          <a href="/privacidad">Privacidad</a>
          <a href="/aviso-legal">Aviso legal</a>
        </div>
      </footer>
    </main>
  );
}
